/**
 * useTableExport Hook - Clean Architecture Export Hook
 *
 * This hook provides a clean, reusable interface for table export functionality.
 * It follows clean architecture principles with separation of concerns.
 */

import {useState, useCallback, useMemo} from 'react';
import {
  UseTableExportReturn,
  ExportConfig,
  ExportFormat,
  ExportOptions,
  ExportResult,
  DEFAULT_EXPORT_OPTIONS
} from '../../types/export.types';
import {exportService} from '../../Services/export/ExportService';

interface UseTableExportProps<T = unknown> {
  data: T[];
  config: ExportConfig<T>;
  options?: ExportOptions;
}

export function useTableExport<T = unknown> ({
  data,
  config,
  options = DEFAULT_EXPORT_OPTIONS
}: UseTableExportProps<T>): UseTableExportReturn {
  const [isExporting, setIsExporting] = useState(false);

  // Memoized computed values
  const canExport = useMemo(() => {
    return data && data.length > 0;
  }, [data]);

  const recordCount = useMemo(() => {
    return data?.length || 0;
  }, [data]);

  // Export function
  const exportData = useCallback(async (format: ExportFormat) => {
    if (!canExport) {
      return {
        success: false,
        error: 'لا توجد بيانات للتصدير',
        recordCount: 0
      };
    }

    setIsExporting(true);

    try {
      let result: ExportResult;

      // Route to appropriate export method based on format
      switch (format) {
        case 'csv':
          result = await exportService.exportToCSV({
            data,
            config,
            format,
            options
          });
          break;

        case 'excel':
          result = await exportService.exportToExcel({
            data,
            config,
            format,
            options
          });
          break;

        default:
          result = {
            success: false,
            error: 'صيغة تصدير غير مدعومة',
            recordCount: 0
          };
      }

      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'خطأ غير معروف',
        recordCount: 0
      };
    } finally {
      setIsExporting(false);
    }
  }, [data, config, options, canExport]);

  return {
    exportData,
    isExporting,
    canExport,
    recordCount
  };
}

/**
 * Hook for creating export configurations
 */
export function useExportConfig<T = unknown> () {
  const createConfig = useCallback((
    columns: Array<{ key: keyof T; title: string; formatter?: (value: unknown, row: T) => string; hidden?: boolean }>,
    filename: string,
    title?: string,
    description?: string
  ): ExportConfig<T> => {
    return {
      columns,
      filename,
      title,
      description
    };
  }, []);

  return {createConfig};
}

/**
 * Hook for export format selection
 */
export function useExportFormats () {
  const formats = useMemo(() => [
    {value: 'csv' as const, label: 'CSV', description: 'ملف CSV قابل للتحرير'},
    {value: 'excel' as const, label: 'Excel', description: 'ملف Excel متقدم'}
  ], []);

  return {formats};
}
